'use client';

import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { CachedWsrvImage } from '@/components/ui/media/cached-wsrv-image';
import { cn } from '@/lib/ui/utils';
import { useReducedMotion } from 'motion/react';
import {
  type ReactNode,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import type { WorkspaceToolParamView } from '../contracts/tool-view';

/** Click-owned rich help. Native popover handles Escape, outside click and top-layer stacking. */
export function WorkspaceInfoPopover({
  label,
  children,
  className,
  onOpenChange,
}: {
  label: string;
  children: ReactNode;
  className?: string;
  onOpenChange?: (open: boolean) => void;
}) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const content = useRef<HTMLDialogElement>(null);
  const [open, setOpen] = useState(false);
  useLayoutEffect(() => {
    if (!open || !content.current) return;
    const position = () => {
      if (!trigger.current || !content.current) return;
      const anchor = trigger.current.getBoundingClientRect();
      const box = content.current.getBoundingClientRect();
      const gap = Number.parseFloat(
        getComputedStyle(content.current).paddingTop
      );
      const width = document.documentElement.clientWidth;
      const height = document.documentElement.clientHeight;
      content.current.style.left = `${Math.max(gap, Math.min(anchor.left, width - box.width - gap))}px`;
      content.current.style.top = `${Math.max(gap, Math.min(anchor.bottom + gap + box.height <= height ? anchor.bottom + gap : anchor.top - box.height - gap, height - box.height - gap))}px`;
    };
    position();
    const observer = new ResizeObserver(position);
    observer.observe(content.current);
    window.addEventListener('resize', position);
    document.addEventListener('scroll', position, true);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', position);
      document.removeEventListener('scroll', position, true);
    };
  }, [open]);
  return (
    <span className={cn('cms-workspace-info-popover', className)}>
      <button
        ref={trigger}
        type="button"
        popoverTarget={id}
        aria-label={label}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        className="cms-block-workspace-workspace-1-workspace-form-controls__paramHintTrigger"
      >
        {renderAppIcon({
          name: 'circle-help',
          className:
            'cms-block-workspace-workspace-1-workspace-form-controls__paramHintIcon',
        })}
      </button>
      <dialog
        ref={content}
        id={id}
        popover="auto"
        aria-label={label}
        className="cms-workspace-info-popover__content"
        onKeyDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
        onToggle={(event) => {
          const nextOpen = event.newState === 'open';
          setOpen(nextOpen);
          onOpenChange?.(nextOpen);
        }}
      >
        {open ? children : null}
      </dialog>
    </span>
  );
}

function HelpContent({
  text,
  imageUrl,
  posterUrl,
  comparisonLabels,
  loadErrorLabel,
}: {
  text: string;
  imageUrl?: string;
  posterUrl?: string;
  comparisonLabels?: { before: string; after: string };
  loadErrorLabel: string;
}) {
  const reduceMotion = useReducedMotion();
  const [failed, setFailed] = useState(false);
  const animated = /\.gif(?:$|[?#])/i.test(imageUrl ?? '');
  const src = reduceMotion && animated ? posterUrl : imageUrl;
  return (
    <>
      {src ? (
        <div className="cms-workspace-info-popover__media">
          {!failed ? (
            <CachedWsrvImage
              showCheckerboard
              loadingSkeleton
              animated={animated && !reduceMotion}
              loading="eager"
              src={src}
              alt=""
              width={480}
              height={480}
              onError={() => setFailed(true)}
            />
          ) : null}
          {!failed && comparisonLabels ? (
            <div className="cms-workspace-info-popover__comparisonLabels">
              <span>{comparisonLabels.before}</span>
              <span>{comparisonLabels.after}</span>
            </div>
          ) : null}
          {failed ? <output>{loadErrorLabel}</output> : null}
        </div>
      ) : null}
      <p>{text}</p>
    </>
  );
}

export function WorkspaceHelpPopover({
  label,
  text,
  preview,
  loadErrorLabel,
}: {
  label: string;
  text: string;
  preview: NonNullable<WorkspaceToolParamView['helpPreview']>;
  loadErrorLabel: string;
}) {
  return (
    <WorkspaceInfoPopover
      label={label}
      className="cms-workspace-info-popover--compact"
    >
      <HelpContent
        key={`${preview.imageUrl ?? ''}:${preview.posterUrl ?? ''}`}
        text={text}
        {...preview}
        loadErrorLabel={loadErrorLabel}
      />
    </WorkspaceInfoPopover>
  );
}
